import React, { createContext, useState, useContext, ReactNode, useEffect, useMemo } from 'react';
import { useEmailAccounts } from '@/hooks/useEmailAccounts';

// Definisce il tipo di un account esposto dal contesto
export interface EmailAccount {
  id: number;
  email: string;
  color: string; // es. "#6B46C1"
  name: string;
  isActive: boolean;
  isPrimary?: boolean;
}

// Context per gestire l'account selezionato
const EmailAccountContext = createContext<{
  accounts: EmailAccount[];
  activeAccount: EmailAccount | null;
  setActiveAccount: (acc: EmailAccount) => void;
  loading: boolean;
}>({
  accounts: [],
  activeAccount: null,
  setActiveAccount: () => {},
  loading: true
});

const ACCOUNT_COLORS = ['#C3BEF0', '#F2C8A8', '#A8D5BA', '#F5E1A4', '#A4D8F5'];

function isAccountActive(account: any): boolean {
  const status = account.status || (account.isActive || account.is_active ? 'active' : 'inactive');
  if (status === 'deleted' || status === 'disabled') return false;
  return account.isActive === true || account.is_active === true || ['active', 'connected'].includes(status);
}

// Provider che carica gli account tramite useEmailAccounts e mantiene activeAccount
export const EmailAccountProvider = ({ children }: { children: ReactNode }) => {
  const { emailAccounts, isLoading } = useEmailAccounts();
  const [activeAccount, setActiveAccount] = useState<EmailAccount | null>(null);

  const accounts = useMemo<EmailAccount[]>(() => {
    return emailAccounts
      .filter(isAccountActive)
      .map((acc: any, i: number) => ({
        id: acc.id,
        email: acc.email,
        name: acc.displayName || acc.display_name || acc.username || acc.email?.split('@')[0] || `Account ${acc.id}`,
        isActive: true,
        isPrimary: acc.isPrimary ?? acc.is_primary ?? false,
        color: ACCOUNT_COLORS[i % ACCOUNT_COLORS.length]
      }));
  }, [emailAccounts]);

  useEffect(() => {
    if (accounts.length === 0) {
      setActiveAccount(null);
      return;
    }

    // Se l'account attivo esiste ancora, mantienilo
    if (activeAccount && accounts.some(acc => acc.id === activeAccount.id)) {
      return;
    }

    // Prima priorità: account marcato come primario nel database
    let primaryAccount = accounts.find(acc => acc.isPrimary === true);

    // Seconda priorità: account @madev.it se non c'è un primario
    if (!primaryAccount) {
      primaryAccount = accounts.find(acc => acc.email?.includes('madev.it'));
    }

    // Terza priorità: primo account disponibile
    if (!primaryAccount) {
      primaryAccount = accounts[0];
    }

    setActiveAccount(primaryAccount);
  }, [accounts, activeAccount]);

  return (
    <EmailAccountContext.Provider value={{ accounts, activeAccount, setActiveAccount, loading: isLoading }}>
      {children}
    </EmailAccountContext.Provider>
  );
};

// Hook comodo
export const useEmailAccount = () => useContext(EmailAccountContext);
